<template>
  <HrmisCrudModule v-bind="config" class="employment-types-enhanced" />
</template>

<script>
import HrmisCrudModule from "./HrmisCrudModule.vue";

export default {
  name: "ModuleEmploymentTypes",
  components: { HrmisCrudModule },
  data() {
    return {
      config: {
        title: "Employment Types",
        subtitle: "Configure Terms of Employment options used in employee forms and bulk upload.",
        icon: "🧾",
        sectionBreadcrumb: "System Management",
        listFunction: "getEmploymentTypeList",
        saveFunction: "saveEmploymentType",
        deleteFunction: "deleteEmploymentType",
        permissionViewCodes: ["EMPLOYMENT_TYPE_VIEW", "EMPLOYMENT_TYPE_MANAGE"],
        permissionManageCodes: ["EMPLOYMENT_TYPE_MANAGE"],
        columns: [
          { key: "name", label: "Name" },
          { key: "code", label: "Code" },
          { key: "requires_personal_email", label: "Requires personal email" },
          { key: "status", label: "Status" },
          { key: "sort_order", label: "Sort" }
        ],
        fields: [
          { key: "name", label: "Employment type name", type: "text", required: true, placeholder: "e.g. Permanent and Pensionable" },
          { key: "code", label: "Code", type: "text", required: false, placeholder: "Auto-generated if blank" },
          { key: "requires_personal_email", label: "Requires personal email", type: "select", options: ["No", "Yes"], default: "No" },
          { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"], default: "Active" },
          { key: "sort_order", label: "Sort order", type: "text", default: 100 }
        ]
      }
    };
  }
};
</script>

<style scoped>
/* ─── Employment Types Enhanced Styling ──────────────────── */
.employment-types-enhanced {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Enhanced toolbar for employment types */
.employment-types-enhanced :deep(.hrmis-toolbar) {
  background: linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%);
  border: 2px solid #a7f3d0;
  box-shadow: 0 4px 16px rgba(5, 150, 105, 0.15);
}

.employment-types-enhanced :deep(.hrmis-page-title) {
  font-size: 20px;
  color: #047857;
}

.employment-types-enhanced :deep(.hrmis-page-title i) {
  font-size: 26px;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

/* Enhanced card styling */
.employment-types-enhanced :deep(.hrmis-card) {
  border: 2px solid #d1fae5;
  box-shadow: 0 4px 20px rgba(5, 150, 105, 0.12);
}

.employment-types-enhanced :deep(.hrmis-card-header) {
  background: linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%);
  border-bottom: 2px solid #a7f3d0;
}

.employment-types-enhanced :deep(.hrmis-card-title) {
  font-size: 17px;
  color: #047857;
}

.employment-types-enhanced :deep(.hrmis-row-count) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #fff;
  font-size: 13px;
  padding: 6px 16px;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
}

/* Enhanced table rows */
.employment-types-enhanced :deep(.hrmis-table tbody tr:hover) {
  background: linear-gradient(90deg, #f0fdf4 0%, #d1fae5 50%, #f0fdf4 100%);
  transform: scale(1.002);
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.1);
}

/* Enhanced status pills */
.employment-types-enhanced :deep(.hrmis-status-approved),
.employment-types-enhanced :deep(.hrmis-status-active) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #fff;
  padding: 6px 14px;
  font-size: 12px;
  box-shadow: 0 2px 6px rgba(5, 150, 105, 0.3);
}

.employment-types-enhanced :deep(.hrmis-status-inactive) {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  color: #fff;
  padding: 6px 14px;
  font-size: 12px;
  box-shadow: 0 2px 6px rgba(100, 116, 139, 0.3);
}

/* Enhanced action buttons */
.employment-types-enhanced :deep(.hrmis-action-edit) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #fff;
  width: 38px;
  height: 38px;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
}

.employment-types-enhanced :deep(.hrmis-action-edit:hover) {
  background: linear-gradient(135deg, #047857 0%, #065f46 100%);
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 16px rgba(5, 150, 105, 0.4);
}

.employment-types-enhanced :deep(.hrmis-action-delete) {
  width: 38px;
  height: 38px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.employment-types-enhanced :deep(.hrmis-action-delete:hover) {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
}

/* Enhanced panel header */
.employment-types-enhanced :deep(.hrmis-panel-header) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 4px 16px rgba(5, 150, 105, 0.3);
}

.employment-types-enhanced :deep(.hrmis-panel-icon) {
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

/* Enhanced form inputs */
.employment-types-enhanced :deep(.hrmis-input:focus),
.employment-types-enhanced :deep(.hrmis-select:focus),
.employment-types-enhanced :deep(.hrmis-textarea:focus) {
  border-color: #059669;
  box-shadow: 0 0 0 4px rgba(5, 150, 105, 0.15);
}

/* Enhanced primary button */
.employment-types-enhanced :deep(.hrmis-btn-primary) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 4px 14px rgba(5, 150, 105, 0.35);
}

.employment-types-enhanced :deep(.hrmis-btn-primary:hover:not(:disabled)) {
  background: linear-gradient(135deg, #047857 0%, #065f46 100%);
  box-shadow: 0 6px 20px rgba(5, 150, 105, 0.45);
  transform: translateY(-3px);
}

/* Enhanced pagination */
.employment-types-enhanced :deep(.hrmis-page-btn:hover:not(:disabled)) {
  background: linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%);
  border-color: #059669;
  color: #047857;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.2);
}

/* Enhanced empty state */
.employment-types-enhanced :deep(.hrmis-empty i) {
  color: #059669;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
}

/* Enhanced loading spinner */
.employment-types-enhanced :deep(.hrmis-spinner) {
  border-top-color: #059669;
  border-width: 4px;
  width: 28px;
  height: 28px;
}
</style>